<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isSaving = ref(false);
const showAddForm = ref(false);

const newTemplate = ref({
  name: '',
  content: '',
  tokens: [],
});

const tokenOptions = [
  { value: 'token', label: 'token' },
  { value: 'token2', label: 'token2' },
  { value: 'token3', label: 'token3' },
  { value: 'token10', label: 'token10' },
  { value: 'token20', label: 'token20' },
];

const templates = computed(() => {
  return props.inbox.message_templates || [];
});

const saveTemplates = async updatedTemplates => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      channel: {
        message_templates: updatedTemplates,
      },
    });
    useAlert(t('INBOX_MGMT.KAVENEGAR_TEMPLATES.SAVE_SUCCESS'));
    showAddForm.value = false;
    newTemplate.value = { name: '', content: '', tokens: [] };
  } catch (error) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

const addTemplate = () => {
  if (!newTemplate.value.name) return;
  const updatedTemplates = [
    ...templates.value,
    { ...newTemplate.value, id: Date.now() },
  ];
  saveTemplates(updatedTemplates);
};

const deleteTemplate = index => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (window.confirm(t('INBOX_MGMT.KAVENEGAR_TEMPLATES.DELETE_CONFIRM'))) {
    const updatedTemplates = [...templates.value];
    updatedTemplates.splice(index, 1);
    saveTemplates(updatedTemplates);
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsFieldSection
      :label="t('INBOX_MGMT.KAVENEGAR_TEMPLATES_TITLE')"
      :help-text="t('INBOX_MGMT.KAVENEGAR_TEMPLATES_SUBHEADER')"
    >
      <div class="flex flex-col gap-4">
        <div
          v-for="(template, index) in templates"
          :key="template.id || index"
          class="p-4 rounded-xl border border-n-weak bg-n-alpha-1 flex justify-between items-start"
        >
          <div class="flex flex-col gap-1">
            <h5 class="text-sm font-semibold text-n-slate-12">
              {{ template.name }}
            </h5>
            <p class="text-xs text-n-slate-11 italic">{{ template.content }}</p>
            <div class="flex gap-1 mt-1">
              <span
                v-for="token in template.tokens"
                :key="token"
                class="px-2 py-0.5 rounded bg-n-alpha-2 text-[10px] font-medium text-n-slate-11 border border-n-weak"
              >
                {{ token }}
              </span>
            </div>
          </div>
          <Button
            variant="faded"
            color="ruby"
            size="sm"
            icon="i-lucide-trash-2"
            @click="deleteTemplate(index)"
          />
        </div>

        <div
          v-if="showAddForm"
          class="p-4 rounded-xl border border-n-blue-4 bg-n-blue-1 flex flex-col gap-4"
        >
          <Input
            v-model="newTemplate.name"
            :label="t('INBOX_MGMT.KAVENEGAR_TEMPLATES.NAME_LABEL')"
            :placeholder="t('INBOX_MGMT.KAVENEGAR_TEMPLATES.NAME_PLACEHOLDER')"
          />
          <TextArea
            v-model="newTemplate.content"
            :label="t('INBOX_MGMT.KAVENEGAR_TEMPLATES.CONTENT_LABEL')"
            :placeholder="
              t('INBOX_MGMT.KAVENEGAR_TEMPLATES.CONTENT_PLACEHOLDER')
            "
          />
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('INBOX_MGMT.KAVENEGAR_TEMPLATES.TOKENS_LABEL') }}
            </label>
            <TagMultiSelectComboBox
              v-model="newTemplate.tokens"
              :options="tokenOptions"
              :placeholder="
                t('INBOX_MGMT.KAVENEGAR_TEMPLATES.TOKENS_PLACEHOLDER')
              "
            />
          </div>
          <div class="flex gap-2 justify-end">
            <Button
              variant="faded"
              color="slate"
              size="sm"
              :label="
                t('INBOX_MGMT.KAVENEGAR_TEMPLATES.CANCEL_BUTTON') || 'Cancel'
              "
              @click="showAddForm = false"
            />
            <Button
              size="sm"
              :label="t('INBOX_MGMT.KAVENEGAR_TEMPLATES.ADD_BUTTON')"
              :is-loading="isSaving"
              :disabled="!newTemplate.name"
              @click="addTemplate"
            />
          </div>
        </div>

        <Button
          v-else
          variant="faded"
          color="blue"
          icon="i-lucide-plus"
          :label="t('INBOX_MGMT.KAVENEGAR_TEMPLATES.ADD_BUTTON')"
          @click="showAddForm = true"
        />
      </div>
    </SettingsFieldSection>
  </div>
</template>
